import { isEqual } from "lodash";
import { shallowEqual, useSelector } from "react-redux";
import { ShownValuesTypes } from "../chart/base/chart-settings/types";
import { usePatchComponentProps } from "hooks/widget-data/component-props-patchers";
import { useGetDatapointsWidgetParams } from "hooks/widget-data/use-datapoints";
import { widgetSelector } from "store/cross-slice/widgetSelector";
import { ChartSelectValueFormatKind, DataGranularityType, SortByTypes } from "store/current-dashboard/types";
import { useCurrentDashboardType, useGetSelectedReferenceDate } from "components/workspaces/hooks/use-current-dashboard";
import { DimensionSelectorEntityType } from "components/modal/widget-workshop/types";
import { getUpdatedDatesForTimePeriod } from "../../time-period/insSettingsTimePeriodHelper";
import { DatapointIdStandardEnum } from "api/ux/src";
import { DashboardTypes } from "components/workspaces/hooks/types";
export const useCurrentWidget = (sectionId, widgetId) => useSelector((state) => widgetSelector(state, widgetId || ""), isEqual);
export const useCurrentWidgetState = (sectionId, widgetId) => useSelector((state) => state.currentDashboard.currentStatus.sections?.[sectionId || ""]?.widgets?.[widgetId || ""], isEqual);
export const useSelectedValues = (sectionId, widgetId) => useSelector((state) => state.currentDashboard.currentStatus.sections?.[sectionId || ""]?.widgets?.[widgetId || ""]?.selectedValues || {}, shallowEqual);
export const useUnSelectedLegends = (sectionId, widgetId) => useSelector((state) => state
    .currentDashboard
    .currentStatus
    .sections?.[sectionId || ""]?.widgets?.[widgetId || ""]?.unSelectedLegends, isEqual);
export const useMasterDetailsChoice = (sectionId, widgetId) => useSelector((state) => state
    .currentDashboard.currentStatus.sections?.[sectionId || ""]?.widgets?.[widgetId || ""]?.masterDetailsChoice || "");
export const useWidgetCurrentChartType = (sectionId, widgetId) => useSelector((state) => state
    .currentDashboard.currentStatus.sections?.[sectionId || ""]?.widgets?.[widgetId || ""]?.chartType || "");
export const useWidgetCurrentSortBy = (sectionId, widgetId) => useSelector((state) => state
    .currentDashboard.currentStatus.sections?.[sectionId || ""]?.widgets?.[widgetId || ""]?.sortBy || SortByTypes.Value);
export const useWidgetCurrentGranularity = (sectionId, widgetId) => useSelector((state) => state
    .currentDashboard?.currentStatus?.sections?.[sectionId || ""]?.widgets?.[widgetId || ""]?.dataGranularity);
const useResolvedDateSettings = (sectionId, widgetId) => {
    const widgetStatus = useSelector((state) => state.currentDashboard?.currentStatus?.sections?.[sectionId || ""]?.widgets?.[widgetId || ""], isEqual);
    const workspaceDateSettings = useSelector((state) => state.currentDashboard?.workspaceSettings?.dateSettings, isEqual);
    return widgetStatus?.isWidgetLevelTimePeriod
        ? widgetStatus?.dateSettingsDefaultValues
        : workspaceDateSettings ?? widgetStatus?.dateSettingsDefaultValues;
};
export const useWidgetCurrentStartDate = (sectionId, widgetId) => {
    const dateSettings = useResolvedDateSettings(sectionId, widgetId);
    const referenceDate = useGetSelectedReferenceDate();
    const { startDateUpdated } = getUpdatedDatesForTimePeriod(dateSettings?.selectedRadio, referenceDate || new Date(), dateSettings?.lastPeriodStep, dateSettings?.lastPeriod, dateSettings?.datePickerStartDate, dateSettings?.datePickerEndDate, dateSettings?.fromPeriod);
    return startDateUpdated;
};
export const useWidgetCurrentEndDate = (sectionId, widgetId) => {
    const dateSettings = useResolvedDateSettings(sectionId, widgetId);
    const referenceDate = useGetSelectedReferenceDate();
    const { endDateUpdated } = getUpdatedDatesForTimePeriod(dateSettings?.selectedRadio, referenceDate || new Date(), dateSettings?.lastPeriodStep, dateSettings?.lastPeriod, dateSettings?.datePickerStartDate, dateSettings?.datePickerEndDate, dateSettings?.fromPeriod);
    return endDateUpdated ?? referenceDate;
};
export const useWidgetCurrentTimePeriod = (sectionId, widgetId) => {
    const dateSettings = useResolvedDateSettings(sectionId, widgetId);
    return dateSettings?.selectedRadio;
};
export const useWidgetCurrentFromPeriod = (sectionId, widgetId) => {
    const dateSettings = useResolvedDateSettings(sectionId, widgetId);
    return dateSettings?.fromPeriod;
};
export const useWidgetCurrentLastPeriod = (sectionId, widgetId) => {
    const dateSettings = useResolvedDateSettings(sectionId, widgetId);
    return dateSettings?.lastPeriod;
};
export const useWidgetCurrentLastPeriodStep = (sectionId, widgetId) => {
    const dateSettings = useResolvedDateSettings(sectionId, widgetId);
    return dateSettings?.lastPeriodStep;
};
export const useWidgetCurrentDatePickerStartDate = (sectionId, widgetId) => {
    const dateSettings = useResolvedDateSettings(sectionId, widgetId);
    return dateSettings?.datePickerStartDate;
};
export const useWidgetCurrentTenor = (sectionId, widgetId) => useSelector((state) => state
    .currentDashboard?.currentStatus?.sections?.[sectionId || ""]?.widgets?.[widgetId || ""]?.defaultTenor);
export const useWidgetCurrentCustomDateRangeStartDate = (sectionId, widgetId) => useSelector((state) => state
    .currentDashboard?.currentStatus?.sections?.[sectionId || ""]?.widgets?.[widgetId || ""]?.customDateRangeStartDate);
export const useWidgetCurrentCustomDateRangeEndDate = (sectionId, widgetId) => useSelector((state) => state
    .currentDashboard?.currentStatus?.sections?.[sectionId || ""]?.widgets?.[widgetId || ""]?.customDateRangeEndDate);
export const useSectionTenor = (sectionId) => useSelector((state) => state
    .currentDashboard?.currentStatus?.sections?.[sectionId || ""]?.sectionTenor);
export const useSectionCustomDateRangeStartDate = (sectionId) => useSelector((state) => state
    .currentDashboard?.currentStatus?.sections?.[sectionId || ""]?.sectionCustomDateRangeStartDate);
export const useSectionCustomDateRangeEndDate = (sectionId) => useSelector((state) => state
    .currentDashboard?.currentStatus?.sections?.[sectionId || ""]?.sectionCustomDateRangeEndDate);
export const useResolvedTenor = (sectionId, widgetId) => {
    const widgetTenor = useWidgetCurrentTenor(sectionId, widgetId);
    const sectionTenor = useSectionTenor(sectionId);
    return widgetTenor ?? sectionTenor;
};
export const useResolvedCustomDateRangeStartDate = (sectionId, widgetId) => {
    const widgetDate = useWidgetCurrentCustomDateRangeStartDate(sectionId, widgetId);
    const sectionDate = useSectionCustomDateRangeStartDate(sectionId);
    return widgetDate ?? sectionDate;
};
export const useResolvedCustomDateRangeEndDate = (sectionId, widgetId) => {
    const widgetDate = useWidgetCurrentCustomDateRangeEndDate(sectionId, widgetId);
    const sectionDate = useSectionCustomDateRangeEndDate(sectionId);
    return widgetDate ?? sectionDate;
};
export const useWidgetCurrentDatePickerEndDate = (sectionId, widgetId) => {
    const dateSettings = useResolvedDateSettings(sectionId, widgetId);
    return dateSettings?.datePickerEndDate;
};
export const useWidgetCurrentReferenceDate = (sectionId, widgetId) => useSelector((state) => state.currentDashboard?.currentStatus?.sections?.[sectionId || ""]?.widgets?.[widgetId || ""]?.referenceDate, isEqual);
export const useWidgetCurrentHasDatePicker = (sectionId, widgetId) => useSelector((state) => state
    .currentDashboard?.currentStatus?.sections?.[sectionId || ""]?.widgets?.[widgetId || ""]?.hasDatePicker);
export const useWidgetCurrentDataScale = (sectionId, widgetId) => useSelector((state) => state
    .currentDashboard?.currentStatus?.sections?.[sectionId || ""]?.widgets?.[widgetId || ""]?.dataScale);
export const useIsWidgetLevelTimePeriod = (sectionId, widgetId) => useSelector((state) => state
    .currentDashboard?.currentStatus?.sections?.[sectionId || ""]?.widgets?.[widgetId || ""]?.isWidgetLevelTimePeriod);
export const useWidgetCurrentPeriodOfAnalysis = (sectionId, widgetId) => useSelector((state) => state
    .currentDashboard?.currentStatus?.sections?.[sectionId || ""]?.widgets?.[widgetId || ""]?.periodOfAnalysis);
export const useWidgetCurrentGranularityType = (sectionId, widgetId) => useSelector((state) => state
    .currentDashboard?.currentStatus?.sections?.[sectionId || ""]?.widgets?.[widgetId || ""]?.dataGranularityType || DataGranularityType.Value);
export const useWidgetCurrentGranularityTitle = (sectionId, widgetId) => useSelector((state) => state
    .currentDashboard?.currentStatus?.sections?.[sectionId || ""]?.widgets?.[widgetId || ""]?.dataGranularityTitle || "");
export const useWidgetCurrentDisplayValueType = (sectionId, widgetId) => useSelector((state) => state
    .currentDashboard.currentStatus.sections?.[sectionId || ""]?.widgets?.[widgetId || ""]?.displayValueType);
export const useWidgetCurrentChartSeriesTypes = (sectionId, widgetId) => useSelector((state) => state.currentDashboard.currentStatus.sections?.[sectionId || ""]?.widgets?.[widgetId || ""]?.chartSeriesTypes, isEqual);
export const useWidgetCurrentExcludeSoldAssets = (sectionId, widgetId) => useSelector((state) => state
    .currentDashboard.currentStatus.sections?.[sectionId || ""]?.widgets?.[widgetId || ""]?.excludeSoldAssets);
export const useWidgetCurrentDisplayOptions = (sectionId, widgetId) => useSelector((state) => state.currentDashboard.currentStatus.sections?.[sectionId || ""]?.widgets?.[widgetId || ""]?.displayOptions, isEqual);
export const useWidgetCurrentGridSwitch = (sectionId, widgetId) => useSelector((state) => state
    .currentDashboard.currentStatus.sections?.[sectionId || ""]?.widgets?.[widgetId || ""]?.gridSwitch || false);
export const useWidgetCurrentTableFilterSelectedValue = (sectionId, widgetId, defaultValue) => useSelector((state) => state.currentDashboard.currentStatus.sections?.[sectionId || ""]?.widgets?.[widgetId || ""]?.tableFilterSelectedValue ?? defaultValue, isEqual);
export const useWidgetComponentName = (sectionId, widgetId) => useSelector((state) => widgetSelector(state, widgetId || "")?.componentName || "");
export const useWidgetComponentProps = (sectionId, widgetId) => useSelector((state) => widgetSelector(state, widgetId || "")?.componentProps || {}, isEqual);
const hasMeasureTimeComponent = (measure) => measure?.timeSeries || (measure?.periods && measure?.periods !== 1);
const isComponentBlocked = (componentName, userSettings) => Boolean(componentName && ["InsDatapointGridWidget", "InsValuePairDatapointWidget"].includes(componentName)) || userSettings?.periodOfAnalysis;
const isChartTypeTimeEnabled = (chartType) => Boolean(chartType &&
    ["InsScatterPlotDatapointChart", "InsWaterfallDatapointChart", "InsTimeSeriesChart"].includes(chartType));
const measuresHasTimeComponentButIsNotProjected = (currentWidget) => {
    const widgetMeasures = currentWidget.componentProps?.datapointParams?.[0]?.measures || [];
    // eslint-disable-next-line no-undef-init
    let hasTimeComponent = undefined;
    for (const measure of widgetMeasures) {
        if (measure?.context?.projectedOnly) {
            return false;
        }
        if (hasMeasureTimeComponent(measure)) {
            hasTimeComponent = true;
        }
    }
    return hasTimeComponent;
};
const selectedValueHasTimeComponent = (currentWidget) => {
    const widgetSelectValues = Object.keys(currentWidget.componentProps?.dataSelectProps?.[0]?.selectValues || {}) || [];
    for (const selectValue of widgetSelectValues) {
        const selectedValue = currentWidget.componentProps?.dataSelectProps?.[0]?.selectValues?.[selectValue] || {};
        if (selectedValue?.defaultValue) {
            const selValMeasure = selectedValue.datapointParams?.[0]?.measures?.[0] ?? {};
            if (hasMeasureTimeComponent(selValMeasure)) {
                return true;
            }
        }
    }
    return false;
};
const isTenorMeasure = (measure) => measure.standardDatapoint === DatapointIdStandardEnum.FundTWR || measure.standardDatapoint === DatapointIdStandardEnum.FundNetIRR;
export const hasTenorMeasures = (params) => params[0]?.measures?.some(isTenorMeasure) ?? false;
export const useIsTenorSettingsAvailable = (sectionId, widgetId) => {
    const originalComponentProps = useWidgetComponentProps(sectionId, widgetId) || {};
    const componentProps = usePatchComponentProps(originalComponentProps, widgetId) || {};
    const params = useGetDatapointsWidgetParams({ sectionId, widgetId }, componentProps);
    const type = useCurrentDashboardType();
    return type !== DashboardTypes.Market && hasTenorMeasures(params);
};
// eslint-disable-next-line
export const useIsTimePeriodAvailable = (sectionId, widgetId) => {
    const originalComponentProps = useWidgetComponentProps(sectionId, widgetId) || {};
    const componentProps = usePatchComponentProps(originalComponentProps, widgetId) || {};
    const currentType = useCurrentDashboardType();
    const currentWidget = useCurrentWidget(sectionId, widgetId) || {};
    if (currentType === DimensionSelectorEntityType.Market) {
        return false;
    }
    if (componentProps.userSettings?.areDateSettingsAvailable !== undefined) {
        return componentProps.userSettings.areDateSettingsAvailable;
    }
    if (isComponentBlocked(currentWidget.componentName, componentProps.userSettings)) {
        return false;
    }
    const measuresHasTimeComponent = measuresHasTimeComponentButIsNotProjected(currentWidget);
    if (measuresHasTimeComponent || selectedValueHasTimeComponent(currentWidget)) {
        return true;
    }
    if (measuresHasTimeComponent === false) {
        return false;
    }
    const chartType = currentWidget.componentProps?.chartType || "";
    return isChartTypeTimeEnabled(chartType);
};
export const usePatchedWidgetComponentProps = (sectionId, widgetId) => {
    const componentProps = useWidgetComponentProps(sectionId, widgetId);
    return usePatchComponentProps(componentProps, sectionId, widgetId);
};
export const useWidgetChildrenId = (sectionId, widgetId) => useSelector((state) => widgetSelector(state, widgetId || "")?.componentProps?.childrenId);
export const useShownValues = (sectionId, widgetId) => useSelector((state) => state
    .currentDashboard
    .currentStatus
    .sections?.[sectionId || ""]?.widgets?.[widgetId || ""]?.shownValues || { filter: ShownValuesTypes.All }, isEqual);
export const useWidgetFooterProperties = (sectionId, widgetId) => {
    const selectedValues = useSelectedValues(sectionId, widgetId), widgetProps = usePatchedWidgetComponentProps(sectionId, widgetId);
    let footer = widgetProps?.footer;
    Object.entries(selectedValues).forEach(([key, value]) => {
        const tempFooter = widgetProps?.dataSelectProps?.find((dataSelectProp) => dataSelectProp.order.toString() === key)?.selectValues[value]?.footer;
        if (tempFooter) {
            footer = tempFooter;
        }
    });
    return footer;
};
export const useWidgetCurrentViewForm = (sectionId, widgetId) => {
    const currentStatusViewForm = useSelector((state) => state.currentDashboard.currentStatus.sections?.[sectionId || ""]?.widgets?.[widgetId || ""]?.viewForm, isEqual);
    const defaultViewForm = ChartSelectValueFormatKind[useWidgetComponentProps(sectionId, widgetId)?.userSettings?.viewForm];
    return currentStatusViewForm ?? defaultViewForm ?? ChartSelectValueFormatKind.amount;
};
export const useWidgetMode = (widgetId) => useSelector((state) => state.entities.Widget?.[widgetId || ""]?.componentProps?.mode);
export const useSectionHideGranularity = (sectionId) => useSelector((state) => state.entities.Section[sectionId || ""]?.hideGranularity ?? false);
